import {
  Button,
  Dropdown,
  EditButton,
  Grid,
  Icons,
  Image,
  List,
  Menu,
  Popconfirm,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { BarcodeStickerButton } from 'components/product/BarcodeStickerButton';
import { CanAccess, useCan, useNavigation } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { ProductVariantShowDrawer, SearchNew } from 'components';
import { ProductEditDrawer } from 'components/product';
import CustomTableWithPagination from 'components/table/custom-table';
import { MobileDataCard } from 'components/table/mobile-data-card';
import { MobileInfiniteVirtualList } from 'components/table/mobile-infinite-virtual-list';
import { IAvatar, IProduct, ISort } from 'interfaces';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import {
  IVariant,
  deleteVariant,
  exportVariants,
  getVariantColumnNames,
  getVariants,
  updateVariantOnRow,
} from 'services/variant';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import {
  camelCaseToWords,
  capitalize,
  csvEscape,
  excelSafeExport,
  UNIQUE_ID_SEPARATOR,
} from 'utilities/shared';
import { CreateColumn, UpdateColumn } from './import';
import { useOrganizationVatRate } from 'hooks/useOrganizationVatRate';
import { priceWithVat } from 'utilities/sale-vat';

interface IProductWithVariant extends IProduct {
  productVariantPhoto?: IAvatar;
}

let INITIAL_VISIBLE_COLUMNS = [
  'sku',
  'image',
  'name',
  'sellingPrice',
  'purchasePrice',
  'type',
  'category',
  'tags',
  'actions',
];

const PRODUCT_COLUMNS = ['productTemplates'];

export const ProductList = () => {
  const { vatRatePercent } = useOrganizationVatRate();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.sm;
  const fetchRequestIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [variants, setVariants] = useState<IVariant[]>([]);
  const [totalVariants, setTotalVariants] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [sort, setSort] = useState<ISort | null>(null);
  const [isShowDrawerVisible, setIsShowDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<IVariant | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedColumns = window.localStorage.getItem('VARIANT_COLUMNS');
    if (savedColumns && Array.isArray(JSON.parse(savedColumns))) {
      return JSON.parse(savedColumns);
    } else {
      return INITIAL_VISIBLE_COLUMNS;
    }
  });

  const [columnNames, setColumnNames] = useState<string[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [productTypeFilter, setProductTypeFilter] = useState<('product' | 'material')[]>([]);
  const [deletingVariantId, setDeletingVariantId] = useState<string | null>(null);

  const { push, edit } = useNavigation();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  useEffect(() => {
    if (!!readPurchasePrice && readPurchasePrice.can === false) {
      INITIAL_VISIBLE_COLUMNS = INITIAL_VISIBLE_COLUMNS.filter((v) => v !== 'purchasePrice');
      setSelectedColumns((cols) => cols.filter((v) => v !== 'purchasePrice'));
    }
  }, [readPurchasePrice]);

  useEffect(() => {
    const fetchVariantColumnNames = async () => {
      try {
        const response = await getVariantColumnNames();
        setColumnNames(
          Array.from(
            new Set([...response.uniqueAttributeNames, ...response.uniqueCustomFieldNames]),
          ),
        );
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    };
    fetchVariantColumnNames();
  }, []);

  const fetchVariants = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    const shouldAppend = isMobile && page > 1;

    if (shouldAppend) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await getVariants({
        searchString: searchText,
        start: page,
        sort,
        limit,
        productType: productTypeFilter[0],
      });

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      setVariants((previous) => (shouldAppend ? previous.concat(response.data) : response.data));
      setTotalVariants(response.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [isMobile, searchText, page, limit, sort, productTypeFilter]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    setPage(1);
    setVariants([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, searchText, sort, productTypeFilter]);

  const onDelete = useCallback(
    async (id: string) => {
      setDeletingVariantId(id);
      try {
        await deleteVariant(id);
        if (isMobile) {
          setVariants((previous) => previous.filter((variant) => variant.productVariantId !== id));
          setTotalVariants((previous) => Math.max(previous - 1, 0));
        } else {
          await fetchVariants();
        }
        notification.success({
          message: 'Success',
          description: 'Deleted variant successfully',
        });
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: err instanceof Error ? err.message : null,
        });
      } finally {
        setDeletingVariantId(null);
      }
    },
    [fetchVariants, isMobile],
  );

  const onTableChange: TableProps<IVariant>['onChange'] = async (
    pagination,
    filters,
    sorter,
    _extra,
  ) => {
    // Sort Handling
    if (sorter && 'field' in sorter && sorter.field && sorter.order) {
      const mappedField = sorter.field === 'productVariantName' ? 'name' : sorter.field;
      const mappedOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC';

      if (!sort || sort.field !== mappedField || sort.order !== mappedOrder) {
        setSort({
          // @ts-ignore
          field: mappedField,
          order: mappedOrder,
        });
      }
    } else if (sort) {
      setSort(null);
    }

    // Filter Handling
    const typeFilter = (filters.type as ('product' | 'material')[]) ?? [];
    if (JSON.stringify(typeFilter) !== JSON.stringify(productTypeFilter)) {
      setProductTypeFilter(typeFilter);
    }

    // Pagination Handling
    const newPage = pagination.current ?? 1;
    const newLimit = pagination.pageSize ?? 10;

    if (newPage !== page) {
      setPage(newPage);
    }

    if (newLimit !== limit) {
      setLimit(newLimit);
    }
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onResetSearch = useCallback(() => {
    setSearchText('');
    setPage(1);
  }, []);

  const menu = (
    <Menu
      multiple
      selectable
      selectedKeys={selectedColumns}
      onClick={({ key }) => {
        if (selectedColumns.includes(key)) {
          // at least one column must be selected
          if (selectedColumns.length === 1) return;

          setSelectedColumns((prev) => {
            const filteredColumns = prev.filter((item) => item !== key);
            window.localStorage.setItem('VARIANT_COLUMNS', JSON.stringify(filteredColumns));
            return filteredColumns;
          });
        } else {
          setSelectedColumns((prev) => {
            const newColumns = [...prev, key];
            window.localStorage.setItem('VARIANT_COLUMNS', JSON.stringify(newColumns));
            return newColumns;
          });
        }
      }}
      style={{
        maxHeight: '300px',
        overflowY: 'scroll',
      }}
    >
      {[...INITIAL_VISIBLE_COLUMNS, ...PRODUCT_COLUMNS, ...columnNames].map((key) => (
        <Menu.Item key={key}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1.5rem',
            }}
          >
            <span>{key}</span>
            {selectedColumns.includes(key) && <Icons.CheckOutlined />}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const tableHeader = (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={isDropdownVisible}
      onVisibleChange={() => setIsDropdownVisible((visible) => !visible)}
    >
      <Button onClick={(e) => e.stopPropagation()}>
        Columns <Icons.DownOutlined />
      </Button>
    </Dropdown>
  );

  const renderRowActions = useCallback(
    (record: IVariant) => {
      const isDeleting = deletingVariantId === record.productVariantId;

      return (
        <Space onClick={(event) => event.stopPropagation()}>
          <Button
            size="small"
            icon={<Icons.EyeOutlined />}
            onClick={() => {
              setIsShowDrawerVisible(true);
              setSelectedVariant(record);
            }}
          />
          <EditButton recordItemId={record.productId} size="small" hideText />
          <BarcodeStickerButton variant={record} vatRatePercent={vatRatePercent} />
          <Popconfirm
            title="Are you sure?"
            okText="Delete"
            okButtonProps={{ loading: isDeleting, disabled: isDeleting }}
            cancelButtonProps={{ disabled: isDeleting }}
            onConfirm={() => onDelete(record.productVariantId)}
          >
            <Button size="small" danger icon={<Icons.DeleteOutlined />} />
          </Popconfirm>
        </Space>
      );
    },
    [deletingVariantId, onDelete, vatRatePercent],
  );

  const renderColumn = useMemo(() => {
    // maintain default columns order & actions column as the last column
    const columns = INITIAL_VISIBLE_COLUMNS.filter((column) => selectedColumns.includes(column));
    let visibleColumns = Array.from(new Set([...columns, ...selectedColumns]));
    if (visibleColumns.includes('actions')) {
      visibleColumns = visibleColumns.filter((column) => column !== 'actions');
      visibleColumns.push('actions');
    }

    return visibleColumns.map((columnName) => {
      switch (columnName) {
        case 'sku':
          return (
            <Table.Column<IVariant>
              key="sku"
              dataIndex="sku"
              title="SKU"
              width={100}
              sorter={(a, b) => {
                if (a.sku < b.sku) return -1;
                if (a.sku > b.sku) return 1;
                return 0;
              }}
            />
          );
        case 'image':
          return (
            <Table.Column<IProductWithVariant>
              key="image"
              dataIndex="avatar"
              title="IMAGE"
              width={100}
              render={(_text, record): React.ReactNode => {
                return (
                  <Image
                    src={record?.productVariantPhoto?.thumbUrl || defaultAvatar}
                    style={{ objectFit: 'cover' }}
                    height={45}
                    width={45}
                    alt={record.name}
                    onClick={(e) => e.stopPropagation()}
                  />
                );
              }}
            />
          );
        case 'name':
          return (
            <Table.Column<IVariant>
              key="name"
              dataIndex="productVariantName"
              title="NAME"
              width={264}
              sorter={(a, b) => {
                if (a.productVariantName < b.productVariantName) return -1;
                if (a.productVariantName > b.productVariantName) return 1;
                return 0;
              }}
              render={(_, record) => {
                return (
                  <Typography.Text style={{ lineHeight: '12px' }}>
                    {record.productVariantName}
                  </Typography.Text>
                );
              }}
            />
          );
        case 'type':
          return (
            <Table.Column<IVariant>
              key="type"
              dataIndex="type"
              title="Type"
              filteredValue={productTypeFilter}
              filterMultiple={false}
              // defaultFilteredValue={['product', 'material']}
              // filterResetToDefaultFilteredValue={true}
              filters={[
                {
                  text: 'Product',
                  value: 'product',
                },
                {
                  text: 'Material',
                  value: 'material',
                },
              ]}
              render={(value) => {
                return <Tag key={value}>{capitalize(value)}</Tag>;
              }}
            />
          );
        case 'productTemplates':
          return (
            <Table.Column<IVariant>
              key="productTemplates"
              dataIndex="productTemplates"
              title="TEMPLATES"
              render={(value) => {
                return value.map((item: any) => (
                  <Tag key={item.templateId}>{item.templateName}</Tag>
                ));
              }}
            />
          );
        case 'purchasePrice':
          return (
            <Table.Column<IVariant>
              dataIndex="purchasePrice"
              width={160}
              title="Default Purchase Price"
              sorter={(a, b) => a.purchasePrice - b.purchasePrice}
            />
          );
        case 'sellingPrice':
          return (
            <Table.Column<IVariant>
              dataIndex="sellingPrice"
              width={vatRatePercent > 0 ? 180 : 140}
              title="Default Selling Price"
              sorter={(a, b) => a.sellingPrice - b.sellingPrice}
              render={(sellingPrice: number) =>
                // With an organization VAT rate (sale.vatRate), show the actual price and the
                // price with VAT.
                vatRatePercent > 0 ? (
                  <span>
                    {sellingPrice}
                    <br />
                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                      With VAT {vatRatePercent}%: {priceWithVat(sellingPrice, vatRatePercent)}
                    </span>
                  </span>
                ) : (
                  sellingPrice
                )
              }
            />
          );
        case 'category':
          return (
            <Table.Column<IVariant>
              key="category"
              dataIndex="category"
              title="CATEGORY"
              render={(value) => {
                return value ? (
                  <Tag key={value.id} color="blue">
                    {value.name}
                  </Tag>
                ) : null;
              }}
            />
          );
        case 'tags':
          return (
            <Table.Column<IVariant>
              key="tags"
              dataIndex="tags"
              title="TAGS"
              render={(value) => {
                return value.map((item: { id: string; name: string }) => (
                  <Tag style={{ margin: 2 }} key={item.id}>
                    {item.name}
                  </Tag>
                ));
              }}
            />
          );
        case 'actions':
          return (
            <Table.Column<IVariant>
              key="actions"
              title="ACTIONS"
              width={170}
              dataIndex="actions"
              render={(_text, record) => renderRowActions(record)}
            />
          );
        default:
          return (
            <Table.Column
              key={columnName}
              dataIndex={columnName}
              title={camelCaseToWords(columnName).toUpperCase()}
            />
          );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeFilter, renderRowActions, selectedColumns, vatRatePercent]);

  const dataSource = useMemo(() => {
    return variants.map(({ customFields, productTemplates, ...rest }) => {
      const newPayload: any = { ...rest, productTemplates: [] };

      const templates = productTemplates || [];
      templates.forEach((template) => {
        const { productTemplateAttributes, templateId, templateName } = template;
        newPayload.productTemplates.push({
          templateId,
          templateName,
        });
        productTemplateAttributes.forEach(({ attributeName, value }) => {
          newPayload[attributeName] = value;
        });
      });

      (customFields ?? []).forEach((field) => {
        const key = Object.keys(field)[0];
        const value = field[key];

        // if key already exists then concatenate the value
        if (newPayload[key]) {
          newPayload[key] = [newPayload[key], value].join(', ');
        } else {
          newPayload[key] = value;
        }
      });

      return newPayload;
    });
  }, [variants]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const products = await exportVariants(searchText, sort);

      const baseHeaders: string[] = [
        UpdateColumn.UniqueID,
        CreateColumn.ProductName,
        UpdateColumn.ProductVariantName,
        CreateColumn.ProductVariantSku,
        CreateColumn.UnitOfMeasure,
        CreateColumn.DefaultSalesPrice,
        CreateColumn.DefaultPurchasePrice,
        CreateColumn.Tags,
        CreateColumn.Category,
        CreateColumn.Make,
        CreateColumn.Buy,
        CreateColumn.Sale,
        CreateColumn.ItemType,
      ];

      const headers = readPurchasePrice?.can
        ? baseHeaders
        : baseHeaders.filter((c) => c !== CreateColumn.DefaultPurchasePrice);

      const csvRow: string[][] = [];
      let maxMetadataCount = 2;
      let maxVariantAttributesCount = 2;

      // First pass to calculate max counts
      products.forEach((product) => {
        const customFieldNames = (product.customFields ?? []).map((f) => f.name);
        maxMetadataCount = Math.max(customFieldNames.length, maxMetadataCount);

        product.variants?.forEach((variant) => {
          maxVariantAttributesCount = Math.max(
            variant.variantAttributes?.length ?? 0,
            maxVariantAttributesCount,
          );
        });
      });

      // Second pass to construct CSV rows
      products.forEach((product) => {
        const customFieldNames = (product.customFields ?? []).map((f) => f.name);
        const variants = product.variants ?? [];

        variants.forEach((variant) => {
          const tags = variant.tags.map((tag) => tag.name).join(',');
          const category = variant.category?.name;
          const measurementUnit = variant.measurementUnit?.name;

          const baseRow: string[] = [
            `${product.id}${UNIQUE_ID_SEPARATOR}${variant.id}`,
            csvEscape(product.name), // JSON.stringify(product.name),
            csvEscape(variant.name), // JSON.stringify(variant.name),
            excelSafeExport(variant.sku), // JSON.stringify(variant.sku),
            JSON.stringify(measurementUnit ?? ''),
            variant.sellingPrice.toString(),
            variant.purchasePrice.toString(),
            JSON.stringify(tags),
            JSON.stringify(category ?? ''),
            variant.make ? 'Yes' : 'No',
            variant.purchase ? 'Yes' : 'No',
            variant.sell ? 'Yes' : 'No',
            capitalize(variant.type),
          ];

          if (!readPurchasePrice?.can) {
            baseRow.splice(6, 1); // Remove purchase price
          }

          // Variant Attributes (pad to max)
          const variantAttributes = variant.variantAttributes ?? [];
          const paddedAttributes: string[] = [];

          for (let i = 0; i < maxVariantAttributesCount; i++) {
            const attr = variantAttributes[i];
            if (attr) {
              const [key, value] = Object.entries(attr)[0] || ['', ''];
              paddedAttributes.push(key, value);
            } else {
              paddedAttributes.push('', '');
            }
          }

          // Metadata (pad to max)
          const metadata: string[] = [];
          for (let i = 0; i < maxMetadataCount; i++) {
            const key = customFieldNames[i];
            const found = (variant.customFields ?? []).find((obj) =>
              Object.keys(obj).some((k) => k.toLowerCase() === key?.toLowerCase()),
            );
            const value = found ? found[Object.keys(found)[0]] : '';
            metadata.push(key ?? '', value);
          }

          csvRow.push(baseRow.concat(paddedAttributes).concat(metadata));
        });
      });

      // Header for variant attributes and metadata
      const attributesAndMetadataHeaders: string[] = [];
      for (let i = 0; i < maxVariantAttributesCount; i++) {
        attributesAndMetadataHeaders.push(`Variant Option ${i + 1}`, `Variant Value ${i + 1}`);
      }
      for (let i = 0; i < maxMetadataCount; i++) {
        attributesAndMetadataHeaders.push(`Meta Data Name ${i + 1}`, `Meta Data Value ${i + 1}`);
      }

      csvRow.unshift(headers.concat(attributesAndMetadataHeaders));
      downloadCSVFile(csvRow);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    }
    setIsExporting(false);
  };

  const pageHeader = (
    <Space wrap size={[8, 8]} style={{ width: '100%' }}>
      <Button
        size="middle"
        icon={<Icons.PlusSquareOutlined />}
        onClick={() => push('/products/create')}
        type="primary"
      >
        Create
      </Button>
      <Button
        size="middle"
        icon={<Icons.ImportOutlined />}
        onClick={() => push('/products/import')}
      >
        Import
      </Button>
      <Button
        size="middle"
        icon={<Icons.ExportOutlined />}
        onClick={handleExport}
        loading={isExporting}
      >
        Export
      </Button>
      <CanAccess resource="activity-history" action="list">
        <Button
          icon={<Icons.HistoryOutlined />}
          onClick={() => push(getActivityHistoryUrl('variant'))}
        >
          Activity
        </Button>
      </CanAccess>
    </Space>
  );

  const handelOnRowClick = (record: IVariant) => {
    setSelectedVariant(record);
    setIsEditDrawerVisible(true);
  };

  const handleProductUpdate = async (data: IVariant) => {
    try {
      await updateVariantOnRow(data.productVariantId, {
        sku: data.sku,
        purchasePrice: data.purchasePrice,
        sellingPrice: data.sellingPrice,
      });
      await fetchVariants();
      notification.success({
        message: 'Success',
        description: 'Updated successfully.',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    }
  };

  const loadMoreVariants = useCallback(() => {
    if (!isMobile || isLoading || isLoadingMore) {
      return;
    }

    if (variants.length >= totalVariants) {
      return;
    }

    setIsLoadingMore(true);
    setPage((previous) => previous + 1);
  }, [isLoading, isLoadingMore, isMobile, totalVariants, variants.length]);

  return (
    <List headerButtons={pageHeader}>
      <MetaData />
      {isShowDrawerVisible && selectedVariant && (
        <ProductVariantShowDrawer
          variant={selectedVariant}
          visible={isShowDrawerVisible}
          onClose={() => {
            setIsShowDrawerVisible(false);
            setSelectedVariant(null);
          }}
        />
      )}
      {/* <ProductVariantEditDrawer
        variant={selectedVariant}
        visible={isEditDrawerVisible}
        onUpdate={handleVariantUpdate}
        onClose={() => {
          setIsEditDrawerVisible(false);
          setSelectedVariant(null);
        }}
      /> */}

      {isEditDrawerVisible && selectedVariant && (
        <ProductEditDrawer
          variant={selectedVariant}
          visible={isEditDrawerVisible}
          onUpdate={handleProductUpdate}
          onClose={() => {
            setIsEditDrawerVisible(false);
            setSelectedVariant(null);
          }}
        />
      )}

      <SearchNew
        placeholder="Search by SKU, Name, Tags, Category"
        size="large"
        className="search"
        index={SearchIndex.ProductVariant}
        groups={['sku', 'name', 'tag', 'category']}
        onSelectOption={onSelectOption}
        onReset={onResetSearch}
      />

      {isMobile ? (
        <div className="soft-list-section mobile-card-page">
          <MobileInfiniteVirtualList
            data={variants}
            total={totalVariants}
            isLoadingInitial={isLoading}
            isLoadingMore={isLoadingMore}
            loadMore={loadMoreVariants}
            itemGap={0}
            renderItem={(record) => (
              <div>
                <MobileDataCard
                  image={
                    <img
                      src={record.productVariantPhoto?.thumbUrl || defaultAvatar}
                      alt={record.productVariantName}
                      loading="lazy"
                    />
                  }
                  title={record.productVariantName}
                  skuValue={record.sku}
                  skuCopyText={record.sku}
                  sellingPrice={record.sellingPrice}
                  actions={[
                    {
                      key: 'view',
                      icon: <Icons.EyeOutlined />,
                      label: 'View',
                      onClick: () => {
                        setSelectedVariant(record);
                        setIsShowDrawerVisible(true);
                      },
                    },
                    {
                      key: 'edit',
                      icon: <Icons.EditOutlined />,
                      label: 'Edit',
                      onClick: () => {
                        edit('products', record.productId);
                      },
                    },
                    {
                      key: 'delete',
                      icon: <Icons.DeleteOutlined />,
                      label: 'Delete',
                      danger: true,
                      disabled: deletingVariantId === record.productVariantId,
                      confirm: {
                        title: 'Are you sure?',
                        okText: 'Delete',
                        cancelText: 'Cancel',
                      },
                      onClick: () => onDelete(record.productVariantId),
                    },
                  ]}
                />
              </div>
            )}
          />
        </div>
      ) : (
        <>
          <Space style={{ margin: '0 0 16px 0' }}>{tableHeader}</Space>

          <CustomTableWithPagination
            total={totalVariants}
            page={page}
            limit={limit}
            onPageChange={handlePaginationChange}
          >
            <Table
              dataSource={dataSource}
              loading={isLoading}
              rowKey={(record) => record.productVariantId}
              scroll={{ x: 'max-content' }}
              pagination={false}
              onChange={onTableChange}
              onRow={(record) => ({
                onClick: (event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('button,a,[role="button"]')) {
                    return;
                  }

                  handelOnRowClick(record);
                },
                style: {
                  cursor: 'pointer',
                },
              })}
            >
              {renderColumn}
            </Table>
          </CustomTableWithPagination>
        </>
      )}
    </List>
  );
};
